/**
 * OAColorVariantsApi - Fetch color variants with caching
 * Uses two-query approach: search for IDs, then bulk fetch details
 * Depends on OAStorefrontApi being loaded first
 */
window.OAColorVariantsApi = {
  cache: new Map(),

  // Query 1: Lightweight search to get product IDs only. 250 is the Storefront maximum:
  // the marker-stripped prefix matches every branding version of the garment, so the old
  // 100 could truncate a large colour family with no error.
  searchQuery: `query searchColorVariants($searchQuery: String!) {
    products(first: 250, query: $searchQuery) {
      nodes {
        id
      }
    }
  }`,

  // Query 2: Bulk fetch full product details by IDs
  detailsQuery: `query getColorVariantDetails($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        title
        handle
        tags
        availableForSale
        variants(first: 1) {
          nodes { id }
        }
        images(first: 1) {
          nodes { url }
        }
      }
    }
  }`,

  /**
   * Extract base title from product title
   * Removes the color suffix " | Color" and every branding marker, so a product reaches the
   * same base whether or not its title still carries the logo suffix — titles are being
   * edited to drop it, and a rail matched on an un-stripped base loses its siblings the
   * moment the two eras are mixed. oaLogoBaseTitle comes from index.layout.js, which loads
   * first and owns the marker list; the old white-only strip stands in if it is absent.
   * Versions are kept apart by the handle instead, in _buildColorString.
   * @param {string} title - Full product title
   * @returns {string} Base title for matching
   */
  extractBaseTitle: function(title) {
    var base = title.split(' | ')[0];
    if (typeof window.oaLogoBaseTitle === 'function') {
      return window.oaLogoBaseTitle(base);
    }
    if (base.includes('with White Logo')) {
      base = base.replace('with White Logo', '').replace(/\s+/g, ' ').trim();
    }
    return base;
  },

  /**
   * Check if a product matches as a color variant
   * @param {Object} product - Product from GraphQL response
   * @param {string} baseTitle - Base title to match against
   * @param {string} excludeHandle - Handle to exclude (current product)
   * @returns {boolean} True if product is a valid color variant
   */
  isColorVariantMatch: function(product, baseTitle, excludeHandle) {
    var productBaseTitle = this.extractBaseTitle(product.title);
    return productBaseTitle === baseTitle &&
           product.handle !== excludeHandle &&
           product.availableForSale;
  },

  /**
   * Transform product to color variant string format
   * @param {Object} product - Product from GraphQL response
   * @returns {string} Format: "color|handle|variantId|imageUrl|available"
   */
  transformToColorString: function(product) {
    var color = product.title.split(' | ')[1] || '';
    var handle = product.handle;
    var variantId = product.variants.nodes[0]
      ? OAStorefrontApi.toNumericId(product.variants.nodes[0].id)
      : '';
    var imageUrl = product.images.nodes[0]
      ? product.images.nodes[0].url.replace(/\?.*$/, '') + '?width=140'
      : '';
    var available = product.availableForSale;
    return color + '|' + handle + '|' + variantId + '|' + imageUrl + '|' + available;
  },

  /**
   * Fetch color variants for a product
   * @param {string} handle - Current product handle
   * @param {string} title - Current product title
   * @returns {Promise<string>} Comma-separated color variant strings (empty string on error/no results)
   */
  fetchColorVariants: function(handle, title) {
    var self = this;
    var baseTitle = this.extractBaseTitle(title);
    var escapedTitle = baseTitle.replace(/"/g, '\\"');

    // Step 1: Search for product IDs
    return OAStorefrontApi.fetch(this.searchQuery, {
      searchQuery: 'title:"' + escapedTitle + '*" AND tag:color-sorting'
    }).then(function(searchData) {
      if (!searchData || !searchData.products || !searchData.products.nodes) {
        return '';
      }

      var productIds = searchData.products.nodes.map(function(p) { return p.id; });
      if (productIds.length === 0) {
        return '';
      }

      // Check cache - find which IDs we need to fetch
      var uncachedIds = productIds.filter(function(id) { return !self.cache.has(id); });

      if (uncachedIds.length === 0) {
        // All cached - filter and return
        return self._buildColorString(productIds, baseTitle, handle, title);
      }

      // Step 2: Bulk fetch uncached products
      return OAStorefrontApi.fetch(self.detailsQuery, { ids: uncachedIds })
        .then(function(detailsData) {
          if (detailsData && detailsData.nodes) {
            detailsData.nodes.forEach(function(product) {
              if (product) {
                self.cache.set(product.id, product);
              }
            });
          }
          return self._buildColorString(productIds, baseTitle, handle, title);
        });
    }).catch(function() {
      return '';
    });
  },

  /**
   * Build the color variant string from cached products
   * @private
   */
  _buildColorString: function(productIds, baseTitle, excludeHandle, title) {
    var self = this;
    // The subject stays in for the filter below: sameLogoVersion reads its tags out of the
    // result set to place it, so excluding it first would leave the YGroup tiebreaker blind
    // and the filter unable to act on a product whose handle carries no marker.
    var sameGarment = productIds
      .map(function(id) { return self.cache.get(id); })
      .filter(function(product) {
        return product && self.extractBaseTitle(product.title) === baseTitle;
      });

    // The base no longer tells the branding versions apart, so the handle does. Without this
    // a grey-logo product and its new-branding twin share a base and land in one rail, and
    // picking a colour silently changes the logo the shopper is looking at.
    if (typeof window.oaSameLogoVersion === 'function') {
      sameGarment = window.oaSameLogoVersion(sameGarment, excludeHandle, title);
    }

    var results = sameGarment
      .filter(function(product) {
        return self.isColorVariantMatch(product, baseTitle, excludeHandle);
      })
      .map(function(product) {
        return self.transformToColorString(product);
      });

    return results.length > 0 ? results.join(',') + ',' : '';
  }
};
